// SharedWorker entry. Each browsing context (tab or Service Worker)
// that opens this worker gets a MessagePort; the worker constructs
// one InMemoryBroker and wires every port up either as a regular
// BrokerConnection (page tabs speaking the envelope protocol) or as
// an SW bridge (Service Worker calling broker methods directly).
//
// Wire shape on the MessagePort:
//   Page tabs (default):
//     { kind: 'envelope', env: Envelope }   bidirectional frame
//     { kind: 'close', code, reason }       either side requesting close
//
//   Service Worker bridge (port opted in via initial 'sw.hello'):
//     SW   →   { kind: 'method', correlationId, method, args }
//     SW   ←   { kind: 'method.response', correlationId, result }
//                or { kind: 'method.error', correlationId, error }
//   Methods exposed: verifyToken(token), exportSession(sessionId).
//   The SW bridge does not participate in node.register / subscribe
//   / state.get — it talks to the broker as a privileged in-process
//   collaborator, not as a wire peer.
//
// All routing decisions happen inside the broker; the worker is just
// a dumb fan-in/fan-out. Persistence is handled by the
// LocalStorageBrokerStore the broker is constructed with.

/// <reference lib="webworker" />

import { InMemoryBroker } from './in_memory_broker';
import { IDBBrokerStore } from './idb_store';
import type { BrokerConnection, Envelope } from './types';

declare const self: SharedWorkerGlobalScope;

interface SharedWorkerGlobalScope {
  addEventListener(
    type: 'connect',
    listener: (ev: { ports: MessagePort[] }) => void,
  ): void;
}

interface IpcFrame {
  kind: 'envelope' | 'close' | 'sw.hello' | 'method' | 'method.response' | 'method.error';
  env?: Envelope;
  code?: number;
  reason?: string;
  // sw bridge fields
  correlationId?: string;
  method?: 'verifyToken' | 'exportSession' | 'exportEvents';
  args?: {
    token?: string;
    sessionId?: string;
    filter?: {
      studyId?: string;
      kind?: string;
      since?: string;
      until?: string;
    };
  };
  result?: unknown;
  error?: string;
}

let connectionSeq = 0;

// SharedWorker can be evaluated in environments that won't actually run
// the connect handler (e.g. type-checking under node, or when the
// module is statically imported by transport_ipc.ts on the main thread
// purely to reuse `bindPort`). The hallmark of an actual SharedWorker
// context is the absence of `window` paired with a `self` that supports
// the 'connect' event — checking `typeof window === 'undefined'` is
// the simplest reliable signal.
const isSharedWorker =
  typeof self !== 'undefined' &&
  typeof (globalThis as { window?: unknown }).window === 'undefined';

if (isSharedWorker) {
  // SharedWorker global scope exposes IndexedDB but NOT
  // localStorage, so the earlier LocalStorageBrokerStore here was a
  // silent no-op — every "persist" call wrote to a null storage,
  // and every restart of this worker booted with an empty broker.
  // Hence the long-standing IPC-mode regression where studies and
  // sessions vanished when the last tab closed. IDBBrokerStore
  // mirrors the same three-key shape (snapshot / events /
  // resources) against IndexedDB, which IS available here.
  const broker = new InMemoryBroker({
    store: new IDBBrokerStore({ dbName: 'helm-broker' }),
  });

  self.addEventListener('connect', (ev) => {
    const port = ev.ports[0];
    if (!port) return;
    bindPort(broker, port);
  });
}

export function bindPort(broker: InMemoryBroker, port: MessagePort): void {
  const id = 'ipc-' + ++connectionSeq;
  let closed = false;
  let mode: 'envelope' | 'sw' | null = null;

  const conn: BrokerConnection = {
    id,
    send: (env: Envelope) => {
      if (closed || mode !== 'envelope') return;
      const frame: IpcFrame = { kind: 'envelope', env };
      try {
        port.postMessage(frame);
      } catch {
        // peer port closed; treat as disconnect.
        teardown();
      }
    },
    close: (code, reason) => {
      if (closed) return;
      const frame: IpcFrame = { kind: 'close', code, reason };
      try {
        port.postMessage(frame);
      } catch {
        // ignore
      }
      teardown();
    },
  };
  // Page-tab connections register with the broker; SW bridge ports
  // do not (they're a privileged side-channel, not protocol peers).
  // We commit to a mode on the first frame.

  function teardown(): void {
    if (closed) return;
    closed = true;
    try {
      port.close();
    } catch {
      // ignore
    }
    if (mode === 'envelope') broker.disconnect(id);
  }

  function handleMethod(frame: IpcFrame): void {
    if (!frame.correlationId || !frame.method) return;
    try {
      let result: unknown = null;
      if (frame.method === 'verifyToken') {
        result = broker.verifyToken(frame.args?.token ?? '');
      } else if (frame.method === 'exportSession') {
        result = broker.exportSession(frame.args?.sessionId ?? '');
      } else if (frame.method === 'exportEvents') {
        result = broker.exportEvents(frame.args?.filter ?? {});
      } else {
        port.postMessage({
          kind: 'method.error',
          correlationId: frame.correlationId,
          error: 'unknown method',
        });
        return;
      }
      port.postMessage({
        kind: 'method.response',
        correlationId: frame.correlationId,
        result,
      });
    } catch (err) {
      port.postMessage({
        kind: 'method.error',
        correlationId: frame.correlationId,
        error: String(err),
      });
    }
  }

  port.addEventListener('message', (ev: MessageEvent<IpcFrame>) => {
    const frame = ev.data;
    if (!frame || typeof frame !== 'object') return;
    // Decide mode on the first frame so the SW handshake is implicit.
    if (mode === null) {
      if (frame.kind === 'sw.hello' || frame.kind === 'method') {
        mode = 'sw';
      } else {
        mode = 'envelope';
        broker.connect(conn);
      }
    }
    if (frame.kind === 'close') {
      teardown();
      return;
    }
    if (mode === 'envelope') {
      if (frame.kind === 'envelope' && frame.env) {
        broker.onMessage(id, frame.env);
      }
      return;
    }
    // SW bridge mode.
    if (frame.kind === 'sw.hello') {
      port.postMessage({
        kind: 'method.response',
        correlationId: 'hello',
        result: { ok: true },
      });
      return;
    }
    if (frame.kind === 'method') {
      handleMethod(frame);
    }
  });
  port.start();
}
